import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    ShieldCheck,
    KeyRound,
    Trash2,
    RefreshCw,
    Copy,
    Check,
    Save,
    PlusCircle,
    Package,
    Wrench,
    User as UserIcon,
} from "lucide-react";
import { fmtTimeAgo } from "../lib/format";
import { toast } from "sonner";

const URGENCY_COLORS = {
    critical: "bg-[var(--rl-urgent)] text-white",
    high: "bg-[#f2b787] text-[#5b3411]",
    medium: "bg-[var(--rl-cream)] text-[var(--rl-text-2)]",
    low: "bg-[var(--rl-sage)] text-[var(--rl-text)]",
};

function TabButton({ active, onClick, children, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                active
                    ? "bg-[var(--rl-primary)] text-white"
                    : "text-[var(--rl-text-2)] hover:bg-[var(--rl-cream)]"
            }`}
        >
            {children}
        </button>
    );
}

function Field({ label, children, className = "" }) {
    return (
        <label className={`block ${className}`}>
            <span className="eyebrow">{label}</span>
            <div className="mt-1.5">{children}</div>
        </label>
    );
}

export default function RescuePortal() {
    const { user, loading: authLoading } = useAuth();
    const [rescue, setRescue] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("profile");
    const [needs, setNeeds] = useState([]);
    const [resources, setResources] = useState([]);
    const [newKey, setNewKey] = useState(null);
    const [copied, setCopied] = useState(false);

    async function load() {
        try {
            const [me, n, r] = await Promise.all([
                api.get("/rescues/me"),
                api.get("/rescues/me/needs"),
                api.get("/rescues/me/resources"),
            ]);
            setRescue(me.data);
            setNeeds(n.data);
            setResources(r.data);
        } catch (e) {
            if (e?.response?.status === 401) {
                // no rescue for this user
            } else {
                toast.error("Could not load your rescue portal");
            }
        }
        setLoading(false);
    }

    useEffect(() => {
        if (authLoading) return;
        if (user) load(); else setLoading(false);
    }, [user, authLoading]);

    if (authLoading || loading) {
        return <div className="p-16 text-center text-[var(--rl-text-2)]">Loading…</div>;
    }
    if (!user) return <Navigate to="/auth?next=/rescue-portal" replace />;
    if (!rescue) {
        return (
            <div className="max-w-[700px] mx-auto px-6 py-24 text-center" data-testid="rescue-portal-empty">
                <div className="w-16 h-16 rounded-full bg-[var(--rl-primary)] text-white flex items-center justify-center mx-auto">
                    <ShieldCheck className="w-8 h-8" />
                </div>
                <h1 className="font-serif text-3xl mt-6">No rescue linked to this account.</h1>
                <p className="text-[var(--rl-text-2)] mt-3">
                    You&apos;re signed in but haven&apos;t registered a rescue. Register your organization to unlock the portal + API access.
                </p>
                <Link to="/join"><Button className="btn-primary rounded-full mt-6 h-11 px-5">Register your rescue</Button></Link>
            </div>
        );
    }

    return (
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12" data-testid="rescue-portal">
            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div>
                    <div className="eyebrow inline-flex items-center gap-2 mb-2"><ShieldCheck className="w-3.5 h-3.5" /> Rescue portal</div>
                    <h1 className="font-serif text-4xl lg:text-5xl tracking-tight" data-testid="rescue-name">{rescue.name}</h1>
                    <div className="mt-2 flex items-center gap-2 flex-wrap text-sm text-[var(--rl-text-2)]">
                        <span>{rescue.location}, {rescue.country}</span>
                        <span>·</span>
                        <span>{rescue.email}</span>
                        {rescue.verified ? (
                            <Badge className="bg-[var(--rl-success)] text-white hover:bg-[var(--rl-success)]">Verified</Badge>
                        ) : (
                            <Badge variant="outline" className="text-[var(--rl-text-2)]">Pending verification</Badge>
                        )}
                    </div>
                </div>
            </div>

            <div className="mt-8 inline-flex bg-white border border-[var(--rl-border)] rounded-full p-1 shadow-sm" data-testid="portal-tabs">
                <TabButton active={tab === "profile"} onClick={() => setTab("profile")} testid="tab-profile"><UserIcon className="inline w-3.5 h-3.5 mr-1" /> Profile</TabButton>
                <TabButton active={tab === "needs"} onClick={() => setTab("needs")} testid="tab-needs"><PlusCircle className="inline w-3.5 h-3.5 mr-1" /> Needs</TabButton>
                <TabButton active={tab === "resources"} onClick={() => setTab("resources")} testid="tab-resources"><Package className="inline w-3.5 h-3.5 mr-1" /> Resources</TabButton>
                <TabButton active={tab === "api"} onClick={() => setTab("api")} testid="tab-api"><KeyRound className="inline w-3.5 h-3.5 mr-1" /> API</TabButton>
            </div>

            <div className="mt-8">
                {tab === "profile" && <ProfileTab rescue={rescue} onSaved={load} />}
                {tab === "needs" && <NeedsTab needs={needs} onChanged={load} />}
                {tab === "resources" && <ResourcesTab resources={resources} onChanged={load} />}
                {tab === "api" && (
                    <ApiKeyTab
                        rescue={rescue}
                        newKey={newKey}
                        setNewKey={setNewKey}
                        copied={copied}
                        setCopied={setCopied}
                    />
                )}
            </div>
        </div>
    );
}

function ProfileTab({ rescue, onSaved }) {
    const [form, setForm] = useState({
        org_name: rescue.name || "",
        contact_person: rescue.contact_person || "",
        phone: rescue.phone || "",
        website: rescue.website || "",
        location: rescue.location || "",
        state: rescue.state || "",
        country: rescue.country || "USA",
        region: rescue.region || "North America",
        description: rescue.description || "",
        donation_url: rescue.donation_url || "",
    });
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => setForm({ ...form, [k]: e?.target?.value ?? e });

    async function save(e) {
        e.preventDefault();
        setBusy(true);
        try {
            await api.patch("/rescues/me", form);
            toast.success("Profile updated");
            onSaved?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save");
        }
        setBusy(false);
    }

    return (
        <form onSubmit={save} className="card-warm rounded-2xl p-6 lg:p-8 grid md:grid-cols-2 gap-4" data-testid="profile-form">
            <Field label="Organization name"><Input value={form.org_name} onChange={set("org_name")} data-testid="profile-name" /></Field>
            <Field label="Contact person"><Input value={form.contact_person} onChange={set("contact_person")} data-testid="profile-contact-person" /></Field>
            <Field label="Phone"><Input value={form.phone} onChange={set("phone")} data-testid="profile-phone" /></Field>
            <Field label="Website"><Input value={form.website} onChange={set("website")} data-testid="profile-website" /></Field>
            <Field label="Location"><Input value={form.location} onChange={set("location")} data-testid="profile-location" /></Field>
            <Field label="Country"><Input value={form.country} onChange={set("country")} data-testid="profile-country" /></Field>
            <Field label="State / Region"><Input value={form.state} onChange={set("state")} data-testid="profile-state" /></Field>
            <Field label="Donation URL"><Input value={form.donation_url} onChange={set("donation_url")} data-testid="profile-donation-url" /></Field>
            <Field label="Description" className="md:col-span-2"><Textarea value={form.description} onChange={set("description")} data-testid="profile-description" /></Field>
            <div className="md:col-span-2 flex justify-end">
                <Button type="submit" disabled={busy} className="btn-primary rounded-full h-11 px-5" data-testid="profile-save"><Save className="w-4 h-4 mr-2" /> {busy ? "Saving…" : "Save changes"}</Button>
            </div>
        </form>
    );
}

function NeedsTab({ needs, onChanged }) {
    const [form, setForm] = useState({ category: "food", urgency: "medium", title: "", description: "", quantity: "" });
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => setForm({ ...form, [k]: e?.target?.value ?? e });
    const setV = (k) => (v) => setForm({ ...form, [k]: v });

    async function add(e) {
        e.preventDefault();
        if (!form.title.trim()) return toast.error("Title is required");
        setBusy(true);
        try {
            await api.post("/rescues/me/needs", { ...form, description: form.description || null, quantity: form.quantity || null });
            toast.success("Need posted to the network");
            setForm({ ...form, title: "", description: "", quantity: "" });
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not post");
        }
        setBusy(false);
    }

    async function remove(id) {
        try {
            await api.delete(`/rescues/me/needs/${id}`);
            toast.success("Need removed");
            onChanged?.();
        } catch (err) {
            toast.error("Could not remove");
        }
    }

    return (
        <div className="grid lg:grid-cols-12 gap-6">
            <form onSubmit={add} className="lg:col-span-6 card-warm rounded-2xl p-6 space-y-4" data-testid="need-form">
                <h3 className="font-serif text-2xl">Post a current need</h3>
                <Field label="Category">
                    <Select value={form.category} onValueChange={setV("category")}>
                        <SelectTrigger data-testid="need-category"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {["food","volunteers","fosters","funds","supplies","medical","transport","other"].map((c) => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>
                <Field label="Urgency">
                    <Select value={form.urgency} onValueChange={setV("urgency")}>
                        <SelectTrigger data-testid="need-urgency"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {["low","medium","high","critical"].map((c) => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>
                <Field label="Title"><Input value={form.title} onChange={set("title")} placeholder="e.g. 150 lbs puppy food" data-testid="need-title" /></Field>
                <Field label="Quantity (optional)"><Input value={form.quantity} onChange={set("quantity")} placeholder="e.g. 150 lbs" data-testid="need-quantity" /></Field>
                <Field label="Description"><Textarea value={form.description} onChange={set("description")} data-testid="need-description" /></Field>
                <Button type="submit" disabled={busy} className="btn-primary rounded-full h-11 px-5" data-testid="need-add"><PlusCircle className="w-4 h-4 mr-2" /> {busy ? "Posting…" : "Post need"}</Button>
            </form>
            <div className="lg:col-span-6">
                <h3 className="font-serif text-2xl mb-3">Open needs</h3>
                <div className="space-y-2" data-testid="need-list">
                    {needs.length === 0 && (
                        <div className="card-warm rounded-lg p-5 text-center text-sm text-[var(--rl-text-2)]">No needs posted yet.</div>
                    )}
                    {needs.map((n) => (
                        <div key={n.id} className="card-warm rounded-lg p-4 flex items-start gap-3">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-baseline gap-2 flex-wrap">
                                    <span className="font-semibold">{n.title}</span>
                                    <Badge className={`text-[10px] ${URGENCY_COLORS[n.urgency]}`}>{n.urgency}</Badge>
                                    <Badge variant="outline" className="text-[10px]">{n.category}</Badge>
                                </div>
                                {n.quantity && <div className="text-xs text-[var(--rl-text-2)] mt-1">{n.quantity}</div>}
                                {n.description && <div className="text-xs text-[var(--rl-text-2)] mt-1">{n.description}</div>}
                                <div className="text-[10px] text-[var(--rl-text-3)] mt-1">{fmtTimeAgo(n.created_at)}</div>
                            </div>
                            <button onClick={() => remove(n.id)} className="text-[var(--rl-text-3)] hover:text-[var(--rl-urgent)]" aria-label="Remove need" data-testid={`need-remove-${n.id}`}>
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function ResourcesTab({ resources, onChanged }) {
    const [form, setForm] = useState({ category: "transport", title: "", description: "", availability: "" });
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => setForm({ ...form, [k]: e?.target?.value ?? e });
    const setV = (k) => (v) => setForm({ ...form, [k]: v });

    async function add(e) {
        e.preventDefault();
        if (!form.title.trim()) return toast.error("Title is required");
        setBusy(true);
        try {
            await api.post("/rescues/me/resources", { ...form, description: form.description || null, availability: form.availability || null });
            toast.success("Resource published");
            setForm({ ...form, title: "", description: "", availability: "" });
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not post");
        }
        setBusy(false);
    }

    async function remove(id) {
        try {
            await api.delete(`/rescues/me/resources/${id}`);
            toast.success("Resource removed");
            onChanged?.();
        } catch (err) {
            toast.error("Could not remove");
        }
    }

    return (
        <div className="grid lg:grid-cols-12 gap-6">
            <form onSubmit={add} className="lg:col-span-6 card-warm rounded-2xl p-6 space-y-4" data-testid="resource-form">
                <h3 className="font-serif text-2xl">Offer a resource</h3>
                <Field label="Category">
                    <Select value={form.category} onValueChange={setV("category")}>
                        <SelectTrigger data-testid="resource-category"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {["transport","foster","volunteers","vet","food","supplies","training","other"].map((c) => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>
                <Field label="Title"><Input value={form.title} onChange={set("title")} placeholder="e.g. 2 spare transport seats weekly" data-testid="resource-title" /></Field>
                <Field label="Availability"><Input value={form.availability} onChange={set("availability")} placeholder="e.g. weekly / on-demand" data-testid="resource-availability" /></Field>
                <Field label="Description"><Textarea value={form.description} onChange={set("description")} data-testid="resource-description" /></Field>
                <Button type="submit" disabled={busy} className="btn-primary rounded-full h-11 px-5" data-testid="resource-add"><PlusCircle className="w-4 h-4 mr-2" /> {busy ? "Posting…" : "Publish resource"}</Button>
            </form>
            <div className="lg:col-span-6">
                <h3 className="font-serif text-2xl mb-3">Resources you offer</h3>
                <div className="space-y-2" data-testid="resource-list">
                    {resources.length === 0 && (
                        <div className="card-warm rounded-lg p-5 text-center text-sm text-[var(--rl-text-2)]">No resources posted yet.</div>
                    )}
                    {resources.map((r) => (
                        <div key={r.id} className="card-warm rounded-lg p-4 flex items-start gap-3">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-baseline gap-2 flex-wrap">
                                    <span className="font-semibold">{r.title}</span>
                                    <Badge variant="outline" className="text-[10px]">{r.category}</Badge>
                                    {r.availability && <span className="text-[10px] text-[var(--rl-text-3)]">· {r.availability}</span>}
                                </div>
                                {r.description && <div className="text-xs text-[var(--rl-text-2)] mt-1">{r.description}</div>}
                                <div className="text-[10px] text-[var(--rl-text-3)] mt-1">{fmtTimeAgo(r.created_at)}</div>
                            </div>
                            <button onClick={() => remove(r.id)} className="text-[var(--rl-text-3)] hover:text-[var(--rl-urgent)]" aria-label="Remove resource" data-testid={`resource-remove-${r.id}`}>
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function ApiKeyTab({ rescue, newKey, setNewKey, copied, setCopied }) {
    const [busy, setBusy] = useState(false);

    async function rotate() {
        if (!window.confirm("Rotate the API key? The current key will be revoked immediately.")) return;
        setBusy(true);
        try {
            const { data } = await api.post("/rescues/me/api-key/rotate");
            setNewKey(data.api_key);
            toast.success("New key issued. Copy it now.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not rotate");
        }
        setBusy(false);
    }

    function copy() {
        if (!newKey) return;
        navigator.clipboard.writeText(newKey);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }

    return (
        <div className="card-warm rounded-2xl p-6 lg:p-8" data-testid="api-tab">
            <div className="eyebrow inline-flex items-center gap-2 mb-2"><KeyRound className="w-3.5 h-3.5" /> API access</div>
            <h3 className="font-serif text-2xl">Personal API key</h3>
            <p className="text-sm text-[var(--rl-text-2)] mt-2 max-w-xl leading-relaxed">
                Use as <code className="font-mono bg-[var(--rl-cream)] px-1.5 py-0.5 rounded">X-API-Key</code> on any endpoint under <code className="font-mono bg-[var(--rl-cream)] px-1.5 py-0.5 rounded">/api/rescues/me/*</code>. Read the <Link to="/developers" className="text-[var(--rl-primary)] font-semibold">API docs</Link> for examples.
            </p>

            <div className="mt-6 rounded-xl bg-[var(--rl-cream)] border border-[var(--rl-border)] p-5">
                <div className="text-xs text-[var(--rl-text-3)] uppercase tracking-widest">Current key prefix</div>
                <div className="font-mono text-lg mt-1" data-testid="api-key-prefix">{rescue.api_key_prefix || "—"}…</div>
                <div className="text-xs text-[var(--rl-text-3)] mt-1">
                    {rescue.api_key_rotated_at ? `Rotated ${fmtTimeAgo(rescue.api_key_rotated_at)}` : "Original key issued at registration"}
                </div>
            </div>

            {newKey ? (
                <div className="mt-5 rounded-xl bg-white border-2 border-[var(--rl-primary)] p-5" data-testid="new-key-panel">
                    <div className="eyebrow text-[var(--rl-primary)]">Your new key · shown once</div>
                    <div className="font-mono text-sm mt-2 break-all">{newKey}</div>
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                        <Button onClick={copy} className="rounded-full h-10 px-4" data-testid="rotate-copy">
                            {copied ? <><Check className="w-4 h-4 mr-2" /> Copied</> : <><Copy className="w-4 h-4 mr-2" /> Copy</>}
                        </Button>
                        <span className="text-xs text-[var(--rl-text-3)]">Store it somewhere safe — we cannot retrieve it later.</span>
                    </div>
                </div>
            ) : null}

            <div className="mt-6 flex flex-wrap gap-3">
                <Button onClick={rotate} disabled={busy} variant="outline" className="rounded-full h-11 px-5 border-[var(--rl-primary)] text-[var(--rl-primary)]" data-testid="rotate-btn">
                    <RefreshCw className="w-4 h-4 mr-2" /> {busy ? "Rotating…" : "Rotate API key"}
                </Button>
                <Link to="/developers"><Button variant="ghost" className="rounded-full h-11 px-5"><Wrench className="w-4 h-4 mr-2" /> Open API docs</Button></Link>
            </div>
        </div>
    );
}
